/**
 * Main JavaScript Entry Point
 * TYPO3 14 Site Package
 */

// Bootstrap components
import Collapse from "bootstrap/js/dist/collapse";
import Dropdown from "bootstrap/js/dist/dropdown";

// Import modules
import { initNavigation } from "./modules/navigation.js";
import {
    initSlideshow,
    initTestimonialsSlideshow,
    initImageGallerySlideshow,
} from "./modules/slideshow.js";
import { initGallery } from "./modules/gallery.js";
import { initTabs } from "./modules/tabs.js";
import { initStageWidgetShadowStyling } from "./modules/stage-widget.js";
import { debounce, log } from "./modules/utils.js";

/**
 * Application initialization
 */
class App {
    constructor() {
        this.isInitialized = false;
        this.modules = [];
    }

    /**
     * Initialize the application
     */
    init() {
        if (this.isInitialized) {
            log("App already initialized", "warn");
            return;
        }

        log("Initializing Site Package...");

        // Register and initialize modules
        this.registerModules();
        this.initModules();

        // Set up global event handlers
        this.setupEventListeners();

        this.isInitialized = true;
        log("Site Package initialized successfully");
    }

    /**
     * Register all modules
     */
    registerModules() {
        this.modules = [
            { name: "navigation", init: initNavigation },
            { name: "slideshow", init: initSlideshow },
            { name: "testimonials-slideshow", init: initTestimonialsSlideshow },
            {
                name: "image-gallery-slideshow",
                init: initImageGallerySlideshow,
            },
            { name: "gallery", init: initGallery },
            { name: "tabs", init: initTabs },
            { name: "stage-widget", init: initStageWidgetShadowStyling },
        ];
    }

    /**
     * Initialize all registered modules
     */
    initModules() {
        this.modules.forEach((module) => {
            try {
                module.init();
                log(`Module "${module.name}" initialized`);
            } catch (error) {
                log(
                    `Failed to initialize module "${module.name}": ${error.message}`,
                    "error",
                );
            }
        });
    }

    /**
     * Set up global event listeners
     */
    setupEventListeners() {
        // Debounced resize handler
        const handleResize = debounce(() => {
            document.documentElement.style.setProperty(
                "--vh",
                `${window.innerHeight * 0.01}px`,
            );
        }, 100);

        window.addEventListener("resize", handleResize);
        handleResize(); // Initial call
    }
}

// Initialize when DOM is ready
const app = new App();

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => app.init());
} else {
    app.init();
}

// Hot Module Replacement for development
if (import.meta.hot) {
    import.meta.hot.accept();
}

export default app;
